import * as THREE from 'three';
import { TILE, CELL, tileKey } from '../../shared/geo.js';
import { CELLS } from '../../shared/tiles.js';
import { groundMaterial, groundMesh, disposeGround } from './terrain.js';
import { treeMeshes, setTreeLod } from './trees.js';
import { propMeshes } from './props.js';
import { indexTile, surfaceAt, wet, collidersNear } from './tileindex.js';

// Which streaming path to use: "cells" (the default since 8 October 2026) has a worker fetch the
// tiles and build 128 m cells, which the main thread only uploads, with far proxies beyond;
// "tiles" builds whole 512 m tiles on the main thread, the path the game shipped with.
// Remembered in the browser like the renderer choice.
export function streamMode(params) {
  const asked = params.get('stream');
  let mode = asked;
  try {
    if (asked) localStorage.setItem('abakwa-stream', asked);
    else mode = localStorage.getItem('abakwa-stream');
  } catch {
    // no storage: the address decides
  }
  return mode === 'tiles' ? 'tiles' : 'cells';
}

const IN_FLIGHT = 2; // cells the worker is building at once
const PROXY_SLACK = TILE; // metres beyond the proxy radius before a far tile is let go
const proxyMaterial = new THREE.MeshLambertMaterial({ vertexColors: true });

// Marks a group and everything in it as never moving, so the scene's matrix pass skips the maths.
export function freeze(group) {
  group.traverse((o) => {
    o.matrixAutoUpdate = false;
    o.updateMatrix();
  });
  group.updateMatrixWorld(true);
}
const SLACK = CELL; // metres beyond the radius before a loaded cell or tile is let go
const MARGIN = 20; // metres the far terrain's hole stays inside the built cells
const TREE_FAR = 150; // metres beyond which a cell's trees are cards (0: always, the Low tier)

// Keeps the cells within `radius` metres of the player loaded, from a worker, and everything else
// freed. Tiles are the data unit (lookups, callbacks, ground pictures); cells are what is drawn.
export class Cells {
  // map: { meta, heightmap, regionId }; materials: { walls, roofs, tar, earth, water, detail, nodes }
  constructor(scene, map, materials) {
    this.scene = scene;
    this.map = map;
    this.materials = materials;
    this.radius = 576; // metres of full detail
    this.trees = true;
    this.tiles = new Map(); // key -> { tx, tz, key, state, data, colliders, buckets, roadCells, ground, material, bitmap }
    this.cells = new Map(); // "tx_tz_cx_cz" -> { tile, tx, tz, cx, cz, state, group, d }
    this.treeFar = TREE_FAR;
    this.known = new Set(map.meta.tiles);
    this.hole = { value: new THREE.Vector4(0, 0, 0, 0) };
    this.onTileBuilt = null;
    this.onTileRemoved = null;
    this.inFlight = 0;
    this.arrived = []; // cell results waiting to be uploaded, at most one per frame
    this.started = false;
    this.physics = null; // set by the game when ?physics=1: the Physics handle
    this.lamps = null; // { x, z, radius }: the town centre, where streetlights stand
    this.proxyRadius = 1600; // metres to which far tiles show as one low mesh each
    this.skips = {}; // tile key -> [building indices] a landmark model stands for
    this.proxies = new Map(); // key -> { tx, tz, mesh } far tiles
  }

  get viewDistance() {
    return this.radius;
  }

  start() {
    this.started = true;
    this.worker = new Worker('worker.js');
    this.worker.onmessage = (e) => this.receive(e.data);
    const heights = this.map.heightmap.data.slice();
    const version = `?v=${this.map.meta.version || this.map.meta.built}`;
    const physics = this.physics ? this.physics.streamPort() : null;
    this.worker.postMessage({ type: 'init', meta: this.map.meta, heights, base: `map/${this.map.regionId}`, version, trees: this.trees, physics, lamps: this.lamps, skips: this.skips }, physics ? [heights.buffer, physics] : [heights.buffer]);
  }

  inBounds(tx, tz) {
    const b = this.map.meta.bounds;
    return (tx + 1) * TILE > b.x0 && tx * TILE < b.x1 && (tz + 1) * TILE > b.z0 && tz * TILE < b.z1;
  }

  // Distance from a point to the nearest edge of a square (0 inside it).
  static distanceToSquare(px, pz, x0, z0, size) {
    const dx = Math.max(x0 - px, 0, px - (x0 + size));
    const dz = Math.max(z0 - pz, 0, pz - (z0 + size));
    return Math.hypot(dx, dz);
  }

  // Call every frame with the player's position. Returns true once every cell in range is drawn.
  update(px, pz) {
    if (!this.started) this.start();
    const R = this.radius;

    // tiles whose square touches the circle are wanted (their data serves lookups and callbacks)
    const tx0 = Math.floor((px - R) / TILE);
    const tx1 = Math.floor((px + R) / TILE);
    const tz0 = Math.floor((pz - R) / TILE);
    const tz1 = Math.floor((pz + R) / TILE);
    for (let tz = tz0; tz <= tz1; tz++) {
      for (let tx = tx0; tx <= tx1; tx++) {
        if (!this.inBounds(tx, tz)) continue;
        const key = tileKey(tx, tz);
        if (this.tiles.has(key)) continue;
        if (Cells.distanceToSquare(px, pz, tx * TILE, tz * TILE, TILE) > R) continue;
        const t = { tx, tz, key, state: 'loading', data: null, colliders: [], buckets: null, roadCells: null, ground: null, material: null, bitmap: null };
        this.tiles.set(key, t);
        this.worker.postMessage({ type: 'tile', key, tx, tz, fetch: this.known.has(key) });
      }
    }
    for (const [key, t] of this.tiles) {
      if (Cells.distanceToSquare(px, pz, t.tx * TILE, t.tz * TILE, TILE) > R + SLACK) this.dropTile(key, t);
    }

    // beyond the cells, every tile to the proxy radius shows as one low mesh
    const P = this.proxyRadius;
    if (P > R) {
      const px0 = Math.floor((px - P) / TILE);
      const px1 = Math.floor((px + P) / TILE);
      const pz0 = Math.floor((pz - P) / TILE);
      const pz1 = Math.floor((pz + P) / TILE);
      for (let tz = pz0; tz <= pz1; tz++) {
        for (let tx = px0; tx <= px1; tx++) {
          if (!this.inBounds(tx, tz)) continue;
          const key = tileKey(tx, tz);
          if (this.proxies.has(key) || !this.known.has(key)) continue;
          if (Cells.distanceToSquare(px, pz, tx * TILE, tz * TILE, TILE) > P) continue;
          this.proxies.set(key, { tx, tz, mesh: null });
          this.worker.postMessage({ type: 'proxy', key, tx, tz, fetch: true });
        }
      }
      for (const [key, p] of this.proxies) {
        if (Cells.distanceToSquare(px, pz, p.tx * TILE, p.tz * TILE, TILE) > P + PROXY_SLACK) this.dropProxy(key, p);
      }
    }

    // cells whose centre is within the radius are wanted, once their tile's data is here
    const wanted = [];
    let complete = true;
    for (const t of this.tiles.values()) {
      if (t.state !== 'built') {
        if (Cells.distanceToSquare(px, pz, t.tx * TILE, t.tz * TILE, TILE) <= R) complete = false;
        continue;
      }
      for (let cz = 0; cz < CELLS; cz++) {
        for (let cx = 0; cx < CELLS; cx++) {
          const mx = t.tx * TILE + (cx + 0.5) * CELL;
          const mz = t.tz * TILE + (cz + 0.5) * CELL;
          const d = Math.hypot(mx - px, mz - pz);
          const key = `${t.key}_${cx}_${cz}`;
          const c = this.cells.get(key);
          if (c) {
            c.d = d;
            if (c.state !== 'built' && d <= R) complete = false;
            continue;
          }
          if (d > R) continue;
          complete = false;
          const cell = { tile: t, tx: t.tx, tz: t.tz, cx, cz, key, state: 'wanted', group: null, d };
          this.cells.set(key, cell);
          wanted.push(cell);
        }
      }
    }
    for (const [key, c] of this.cells) {
      if (c.d > R + SLACK) {
        if (c.state === 'wanted') this.cells.delete(key);
        else if (c.state === 'built') {
          this.disposeCell(c);
          this.cells.delete(key);
        }
        // a cell the worker is still building is kept until its result arrives
      }
    }

    // ask the worker for the nearest cells first, a couple at a time
    if (this.inFlight < IN_FLIGHT) {
      const queue = [];
      for (const c of this.cells.values()) if (c.state === 'wanted') queue.push(c);
      queue.sort((a, b) => a.d - b.d);
      for (const c of queue) {
        if (this.inFlight >= IN_FLIGHT) break;
        c.state = 'building';
        this.inFlight++;
        this.worker.postMessage({ type: 'cell', key: c.tile.key, tx: c.tx, tz: c.tz, cx: c.cx, cz: c.cz });
      }
    }

    // hand one finished cell to the GPU per frame, nearest first
    if (this.arrived.length) {
      this.arrived.sort((a, b) => a.cell.d - b.cell.d);
      const { cell, m } = this.arrived.shift();
      if (this.cells.get(cell.key) === cell) this.upload(cell, m);
    }

    this.updateHole(px, pz);
    // far cells show their trees as cards; checked every few frames
    this.lodTick = (this.lodTick || 0) + 1;
    if (this.lodTick % 5 === 0) {
      for (const c of this.cells.values()) {
        if (!c.group || !c.group.userData.trees) continue;
        const cx = c.tx * TILE + c.cx * CELL + CELL / 2;
        const cz = c.tz * TILE + c.cz * CELL + CELL / 2;
        const far = Math.hypot(cx - px, cz - pz) > this.treeFar;
        if (c.group.userData.treesFar !== far) {
          c.group.userData.treesFar = far;
          setTreeLod(c.group, far);
        }
      }
    }
    return complete;
  }

  receive(m) {
    if (m.type === 'proxy') {
      const p = this.proxies.get(m.key);
      if (!p || p.mesh) return;
      const a = m.arrays;
      if (!a.pos.length) {
        p.mesh = false; // an empty tile: nothing to draw, nothing to drop
        return;
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(a.pos, 3));
      g.setAttribute('normal', new THREE.BufferAttribute(a.nor, 3));
      g.setAttribute('color', new THREE.BufferAttribute(a.col, 3));
      const mesh = new THREE.Mesh(g, proxyMaterial);
      mesh.position.set(p.tx * TILE, 0, p.tz * TILE);
      mesh.matrixAutoUpdate = false;
      mesh.updateMatrix();
      mesh.receiveShadow = true;
      p.mesh = mesh;
      this.scene.add(mesh);
      return;
    }
    if (m.type === 'tile') {
      const t = this.tiles.get(m.key);
      if (!t) {
        m.ground.close();
        return;
      }
      t.data = m.data;
      t.bitmap = m.ground;
      const tex = new THREE.Texture(m.ground);
      tex.flipY = false;
      tex.colorSpace = THREE.SRGBColorSpace;
      tex.anisotropy = 4;
      tex.needsUpdate = true;
      t.ground = tex;
      t.material = groundMaterial(tex, this.materials.detail, !!this.materials.nodes);
      indexTile(t, this.map.heightmap);
      t.state = 'built';
      if (this.onTileBuilt) this.onTileBuilt(t);
    } else if (m.type === 'cell') {
      this.inFlight--;
      const cell = this.cells.get(`${m.key}_${m.cx}_${m.cz}`);
      if (!cell || cell.state !== 'building' || m.missing) {
        if (cell && cell.state === 'building') this.cells.delete(cell.key);
        return;
      }
      cell.state = 'arrived';
      this.arrived.push({ cell, m });
    }
  }

  // Makes the meshes of one cell from the worker's arrays. The arrays are used as they are, so
  // nothing is copied on this thread.
  upload(cell, m) {
    const t = cell.tile;
    const mat = this.materials;
    const group = new THREE.Group();
    group.position.set(t.tx * TILE, 0, t.tz * TILE);
    const ground = groundMesh(m.terrain, t.material);
    ground.position.set(cell.cx * CELL, 0, cell.cz * CELL);
    group.add(ground);
    const add = (name, material, cast, receive) => {
      const p = m.parts[name];
      if (!p) return;
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.BufferAttribute(p.pos, 3));
      g.setAttribute('normal', new THREE.BufferAttribute(p.nor, 3));
      g.setAttribute('color', new THREE.BufferAttribute(p.col, 3));
      g.setAttribute('uv', new THREE.BufferAttribute(p.uv, 2));
      if (p.lit) g.setAttribute('lit', new THREE.BufferAttribute(p.lit, 1));
      const mesh = new THREE.Mesh(g, material);
      mesh.castShadow = cast;
      mesh.receiveShadow = receive;
      group.add(mesh);
    };
    add('walls', mat.walls, true, true);
    add('roofs', mat.roofs, true, true);
    add('tar', mat.tar, false, true);
    add('earth', mat.earth, false, true);
    add('water', mat.water, false, false);
    add('pools', mat.pools, false, false);
    if (m.trees) {
      const trees = treeMeshes(m.trees, 0, 0);
      if (trees) {
        group.add(trees);
        group.userData.trees = trees.userData.trees; // the near/card pairs, for setTreeLod
      }
    }
    if (m.props) {
      const props = propMeshes(m.props, 0, 0);
      if (props) group.add(props);
      cell.props = m.props; // the lists stay, so kiosks and the like can be found by position
    }
    freeze(group);
    cell.group = group;
    cell.state = 'built';
    this.scene.add(group);
  }

  disposeCell(c) {
    if (this.physics) this.physics.dropCell(c.key);
    if (!c.group) return;
    this.scene.remove(c.group);
    c.group.traverse((o) => {
      if (!o.isMesh) return;
      if (o.isInstancedMesh) {
        o.dispose(); // trees share their shape and material; only the instance data is the cell's
        return;
      }
      o.geometry.dispose();
    });
    c.group = null;
  }

  dropProxy(key, p) {
    if (p.mesh) {
      this.scene.remove(p.mesh);
      p.mesh.geometry.dispose();
    }
    this.proxies.delete(key);
    // the worker keeps the data only while a cell or proxy of the tile is wanted
    if (!this.tiles.has(key)) this.worker.postMessage({ type: 'drop', key });
  }

  dropTile(key, t) {
    for (const [ck, c] of this.cells) {
      if (c.tile !== t) continue;
      if (c.state === 'built') this.disposeCell(c);
      if (c.state !== 'building') this.cells.delete(ck);
    }
    if (t.ground) {
      disposeGround(t.material);
      t.bitmap.close();
    }
    if (!this.proxies.has(key)) this.worker.postMessage({ type: 'drop', key });
    this.tiles.delete(key);
    if (t.state === 'built' && this.onTileRemoved) this.onTileRemoved(t);
  }

  // The nearest prop of a kind (kiosk, drum, sign, lamp, pole) within r metres, as { x, z, d },
  // or null. The lists hold five numbers a prop: x, y, z (tile-local metres), yaw and a tint.
  propsNear(kind, x, z, r) {
    let best = null;
    for (const c of this.cells.values()) {
      const list = c.props && c.props[kind];
      if (!list) continue;
      const ox = c.tx * TILE;
      const oz = c.tz * TILE;
      if (Math.abs(ox + c.cx * CELL + CELL / 2 - x) > CELL + r || Math.abs(oz + c.cz * CELL + CELL / 2 - z) > CELL + r) continue;
      for (let i = 0; i < list.length; i += 5) {
        const d = Math.hypot(ox + list[i] - x, oz + list[i + 2] - z);
        if (d < r && (!best || d < best.d)) best = { x: ox + list[i], z: oz + list[i + 2], d };
      }
    }
    return best;
  }

  // The far terrain is hidden under the largest square of built cells around the player.
  updateHole(px, pz) {
    const ccx = Math.floor(px / CELL);
    const ccz = Math.floor(pz / CELL);
    const maxR = Math.floor(this.radius / CELL);
    let size = -1;
    for (let rr = 0; rr <= maxR; rr++) {
      let all = true;
      for (let dz = -rr; dz <= rr && all; dz++) {
        for (let dx = -rr; dx <= rr; dx++) {
          const gx = ccx + dx;
          const gz = ccz + dz;
          const tx = Math.floor(gx / CELLS);
          const tz = Math.floor(gz / CELLS);
          if (!this.inBounds(tx, tz)) continue;
          const c = this.cells.get(`${tileKey(tx, tz)}_${gx - tx * CELLS}_${gz - tz * CELLS}`);
          if (!c || c.state !== 'built') {
            all = false;
            break;
          }
        }
      }
      if (!all) break;
      size = rr;
    }
    if (size < 0) this.hole.value.set(0, 0, 0, 0);
    else this.hole.value.set((ccx - size) * CELL + MARGIN, (ccz - size) * CELL + MARGIN, (ccx + size + 1) * CELL - MARGIN, (ccz + size + 1) * CELL - MARGIN);
  }

  surfaceAt(x, z) {
    return surfaceAt(this.tiles, x, z);
  }

  wet(x, z, margin = 0) {
    return wet(this.map, this.tiles, x, z, margin);
  }

  collidersNear(x, z, out) {
    return collidersNear(this.tiles, x, z, out);
  }
}
